import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Loader2, AlertCircle, Plus, CheckCircle, Calendar } from 'lucide-react';
import appliBg from '../../assets/appliBg.svg';
import { Button } from '../../components/ui/button';
import { useSubmitEmployeeFormMutation, useGetInvestigationByLinkQuery } from '../../store/api/apiSlice';

// ─── Types ───────────────────────────────────────────────────────────────────

interface AddressEntry {
  date: string;
  address: string;
}

interface RadioWithDetailsValue {
  answer: 'yes' | 'no' | '';
  list: string;
  reasons: string;
}

interface WorkHistoryEntry {
  employer: string;
  address: string;
  contact: string;
  startDate: string;
  endDate: string;
}

interface EducationHistoryEntry {
  institution: string;
  address: string;
  contact: string;
  startDate: string;
  endDate: string;
}

interface FormData {
  // Step 0: Personal Identification
  fullLegalName: string;
  previousNames: string;
  dateOfBirth: string;
  placeOfBirth: string;
  nationality: string;
  otherNationalities: string;
  nationalIdNumber: string;
  stateOfOrigin: string;
  localGovernmentArea: string;
  addresses: AddressEntry[];
  workHistory: WorkHistoryEntry[];
  educationHistory: EducationHistoryEntry[];

  // Step 1: Criminal & Law Enforcement Disclosure
  criminalConviction: RadioWithDetailsValue;
  underInvestigation: RadioWithDetailsValue;
  financialCrimes: RadioWithDetailsValue;
  pleaBargain: RadioWithDetailsValue;
  hasBeenDisengaged: RadioWithDetailsValue;

  // Step 2: Financial & Directorship History
  directorLiquidated: RadioWithDetailsValue;
  disqualifiedDirector: RadioWithDetailsValue;
  currentDirector: RadioWithDetailsValue;

  // Step 3: Professional Discipline
  disciplinaryProceedings: RadioWithDetailsValue;
  licenceRevoked: RadioWithDetailsValue;
  regulatorySanction: RadioWithDetailsValue;

  // Step 4: Political Exposure
  isPep: string;
  hasHeldPublicOffice: RadioWithDetailsValue;
  isRelatedToPep: string;
  hasProcurementInvestigation: string;

  // Step 5: Workplace Conduct
  hasMisconductDismissal: RadioWithDetailsValue;
  hasWorkplaceInvestigation: string;
  hasWorkedWithVulnerableGroups: string;
  hasLeadershipBar: RadioWithDetailsValue;

  // Step 6: Civil Claims
  hasShareholderDisputes: string;
  hasJudgments: string;

  // Step 7: Media Disclosure
  hasAdverseMedia: RadioWithDetailsValue;
  hasPublicAccusation: string;
  hasUndisclosedReputationalMatters: string;

  // Step 8: Conflict of Interest
  hasBusinessConflict: RadioWithDetailsValue;
  hasProcurementRelationship: RadioWithDetailsValue;
  hasCompetingBoard: RadioWithDetailsValue;

  // Step 9: Data Protection & Consent
  consentItems: Record<string, boolean>;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const TABS = [
  'Personal Identification',
  'Address & History',
  'Criminal & Law Enforcement Disclosure',
  'Financial & Directorship History',
  'Professional Discipline',
  'Political Exposure',
  'Workplace Conduct',
  'Civil Claims',
  'Media Disclosure',
  'Conflict Of Interest',
  'Data Protection & Consent',
];

const CONSENT_ITEMS = [
  'Criminal background verification',
  'Employment reference checks',
  'Reference/Guarantor verification',
  'Address verification',
  'Social media review',
  'Court record searches',
  'Surprise Lifestyle Audit',
  'Surprise Procurement Integrity Checks',
];

const TOTAL_STEPS = 11;

// Map step index to the active tab index in the bottom navigation
const STEP_TO_TAB: Record<number, number> = {
  0: 0,
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
  7: 7,
  8: 8,
  9: 9,
  10: 10,
};

const DEFAULT_RADIO_DETAILS: RadioWithDetailsValue = { answer: '', list: '', reasons: '' };

const INITIAL_FORM_DATA: FormData = {
  fullLegalName: '',
  previousNames: '',
  dateOfBirth: '',
  placeOfBirth: '',
  nationality: '',
  otherNationalities: '',
  nationalIdNumber: '',
  stateOfOrigin: '',
  localGovernmentArea: '',
  addresses: [
    { date: '', address: '' },
  ],
  workHistory: [],
  educationHistory: [],
  criminalConviction: { ...DEFAULT_RADIO_DETAILS },
  underInvestigation: { ...DEFAULT_RADIO_DETAILS },
  financialCrimes: { ...DEFAULT_RADIO_DETAILS },
  pleaBargain: { ...DEFAULT_RADIO_DETAILS },
  hasBeenDisengaged: { ...DEFAULT_RADIO_DETAILS },
  directorLiquidated: { ...DEFAULT_RADIO_DETAILS },
  disqualifiedDirector: { ...DEFAULT_RADIO_DETAILS },
  currentDirector: { ...DEFAULT_RADIO_DETAILS },
  disciplinaryProceedings: { ...DEFAULT_RADIO_DETAILS },
  licenceRevoked: { ...DEFAULT_RADIO_DETAILS },
  regulatorySanction: { ...DEFAULT_RADIO_DETAILS },
  // Political Exposure
  isPep: '',
  hasHeldPublicOffice: { ...DEFAULT_RADIO_DETAILS },
  isRelatedToPep: '',
  hasProcurementInvestigation: '',
  // Workplace Conduct
  hasMisconductDismissal: { ...DEFAULT_RADIO_DETAILS },
  hasWorkplaceInvestigation: '',
  hasWorkedWithVulnerableGroups: '',
  hasLeadershipBar: { ...DEFAULT_RADIO_DETAILS },
  // Civil Claims
  hasShareholderDisputes: '',
  hasJudgments: '',
  // Media Disclosure
  hasAdverseMedia: { ...DEFAULT_RADIO_DETAILS },
  hasPublicAccusation: '',
  hasUndisclosedReputationalMatters: '',
  // Conflict of Interest
  hasBusinessConflict: { ...DEFAULT_RADIO_DETAILS },
  hasProcurementRelationship: { ...DEFAULT_RADIO_DETAILS },
  hasCompetingBoard: { ...DEFAULT_RADIO_DETAILS },
  // Consent
  consentItems: Object.fromEntries(CONSENT_ITEMS.map((item) => [item, false])),
};

// ─── Reusable Sub-components ─────────────────────────────────────────────────

function TextInput({
  label,
  value,
  onChange,
  placeholder,
  half = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  half?: boolean;
}) {
  return (
    <div className={half ? 'flex-1 min-w-0' : 'w-full'}>
      <label className="block text-sm font-bold text-[#151515] mb-1 leading-6">{label}</label>
      <div className="bg-white border border-[#e0e0e0] rounded-lg h-12 px-3 flex items-center">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full text-xs text-[#4e4d4d] bg-transparent outline-none placeholder:text-[#4e4d4d]"
        />
      </div>
    </div>
  );
}

function DateInput({
  label,
  value,
  onChange,
  placeholder = 'DD/MM/YYYY',
  half = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  half?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Format ISO date (yyyy-mm-dd) to display format (DD MMM YYYY)
  const formatDisplay = (isoDate: string) => {
    if (!isoDate) return '';
    const d = new Date(isoDate + 'T00:00:00');
    if (isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className={half ? 'flex-1 min-w-0' : 'w-full'}>
      <label className="block text-sm font-bold text-[#151515] mb-1 leading-6">{label}</label>
      <div
        className="bg-white border border-[#e0e0e0] rounded-lg h-12 px-3 flex items-center cursor-pointer relative"
        onClick={() => inputRef.current?.showPicker()}
      >
        <span className={`flex-1 text-xs ${value ? 'text-[#1b1b1b] font-semibold' : 'text-[#4e4d4d]'}`}>
          {value ? formatDisplay(value) : placeholder}
        </span>
        <Calendar className="w-4 h-4 text-[#4e4d4d] shrink-0" />
        <input
          ref={inputRef}
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />
      </div>
    </div>
  );
}

function DateRangeInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const startRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLInputElement>(null);

  // Parse stored value "yyyy-mm-dd - yyyy-mm-dd" into start/end
  const parts = value.split(' - ');
  const startVal = parts[0] || '';
  const endVal = parts[1] || '';

  const formatDisplay = (isoDate: string) => {
    if (!isoDate) return '';
    const d = new Date(isoDate + 'T00:00:00');
    if (isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleChange = (start: string, end: string) => {
    if (start || end) {
      onChange(`${start} - ${end}`);
    } else {
      onChange('');
    }
  };

  const displayText = () => {
    if (!startVal && !endVal) return '';
    return `${formatDisplay(startVal)} - ${formatDisplay(endVal)}`;
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-bold text-[#151515] mb-1 leading-6">{label}</label>
      <div className="bg-white border border-[#e0e0e0] rounded-lg h-12 px-3 flex items-center gap-1">
        <div
          className="flex-1 flex items-center cursor-pointer relative min-w-0"
          onClick={() => startRef.current?.showPicker()}
        >
          <span className={`text-xs truncate ${startVal ? 'text-[#1b1b1b] font-semibold' : 'text-[#4e4d4d]'}`}>
            {startVal ? formatDisplay(startVal) : 'Start date'}
          </span>
          <input
            ref={startRef}
            type="date"
            value={startVal}
            onChange={(e) => handleChange(e.target.value, endVal)}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
        </div>
        <span className="text-xs text-[#4e4d4d] shrink-0">–</span>
        <div
          className="flex-1 flex items-center cursor-pointer relative min-w-0"
          onClick={() => endRef.current?.showPicker()}
        >
          <span className={`text-xs truncate ${endVal ? 'text-[#1b1b1b] font-semibold' : 'text-[#4e4d4d]'}`}>
            {endVal ? formatDisplay(endVal) : 'End date'}
          </span>
          <input
            ref={endRef}
            type="date"
            value={endVal}
            onChange={(e) => handleChange(startVal, e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
        </div>
        <Calendar className="w-4 h-4 text-[#4e4d4d] shrink-0" />
      </div>
    </div>
  );
}

function TextArea({
  placeholder,
  value,
  onChange,
  rows = 3,
}: {
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <div className="bg-white border border-[#e0e0e0] rounded-lg px-3 py-2">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full text-xs text-[#4e4d4d] bg-transparent outline-none resize-none placeholder:text-[#4e4d4d]"
      />
    </div>
  );
}

function RadioGroup({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: 'yes' | 'no') => void;
}) {
  return (
    <div className="flex items-center gap-5">
      <label className="flex items-center gap-2 cursor-pointer w-[68px]">
        <span className="relative w-6 h-6 flex items-center justify-center">
          <span
            className={`absolute inset-[16.67%] rounded-full border ${
              value === 'yes' ? 'border-[#f60]' : 'border-[#6c6c6c]'
            }`}
          />
          {value === 'yes' && (
            <span className="absolute inset-[33.33%] rounded-full bg-[#f60] border border-[#f60]" />
          )}
        </span>
        <input
          type="radio"
          checked={value === 'yes'}
          onChange={() => onChange('yes')}
          className="sr-only"
        />
        <span className="text-sm text-[#1b1b1b]">Yes</span>
      </label>
      <label className="flex items-center gap-2 cursor-pointer w-[68px]">
        <span className="relative w-6 h-6 flex items-center justify-center">
          <span
            className={`absolute inset-[16.67%] rounded-full border ${
              value === 'no' ? 'border-[#f60]' : 'border-[#6c6c6c]'
            }`}
          />
          {value === 'no' && (
            <span className="absolute inset-[33.33%] rounded-full bg-[#f60] border border-[#f60]" />
          )}
        </span>
        <input
          type="radio"
          checked={value === 'no'}
          onChange={() => onChange('no')}
          className="sr-only"
        />
        <span className="text-sm text-[#1b1b1b]">No</span>
      </label>
    </div>
  );
}

function RadioWithDetails({
  label,
  value,
  onChange,
  showListField = true,
}: {
  label: string;
  value: RadioWithDetailsValue;
  onChange: (v: RadioWithDetailsValue) => void;
  showListField?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-bold text-[#151515] leading-6">{label}</label>
      <RadioGroup
        value={value.answer}
        onChange={(v) => onChange({ ...value, answer: v })}
      />
      {value.answer === 'yes' && (
        <>
          {showListField && (
            <div className="bg-white border border-[#e0e0e0] rounded-lg h-12 px-3 flex items-center">
              <input
                type="text"
                value={value.list}
                onChange={(e) => onChange({ ...value, list: e.target.value })}
                placeholder="List them"
                className="w-full text-xs text-[#4e4d4d] bg-transparent outline-none placeholder:text-[#4e4d4d]"
              />
            </div>
          )}
          <TextArea
            placeholder="Give reasons"
            value={value.reasons}
            onChange={(v) => onChange({ ...value, reasons: v })}
          />
        </>
      )}
    </div>
  );
}

function Checkbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <span className="relative w-6 h-6 flex items-center justify-center shrink-0">
        <span
          className={`absolute inset-[16.67%] rounded border-[1.5px] ${
            checked ? 'border-[#151515] bg-[#151515]' : 'border-[#333]'
          }`}
        />
        {checked && (
          <CheckCircle className="absolute w-3 h-3 text-white z-10" />
        )}
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <span className="text-sm text-[#1b1b1b]">{label}</span>
    </label>
  );
}

// ─── Step Content Components ─────────────────────────────────────────────────

function StepPersonalIdentification({
  formData,
  setFormData,
}: {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
  const updateField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="flex flex-col gap-6 text-left">
      {/* Section: Personal Identification */}
      <div>
        <h2 className="text-xl md:text-2xl font-bold text-[#0f0f0f] leading-8 mb-6 md:mb-[55px]">Personal Identification</h2>
        <div className="flex flex-col gap-0">
          {/* Row 1 */}
          <div className="flex flex-col sm:flex-row gap-2">
            <TextInput
              label="Full Legal Name"
              value={formData.fullLegalName}
              onChange={(v) => updateField('fullLegalName', v)}
              placeholder="Enter your full legal name"
              half
            />
            <TextInput
              label="Previous Names"
              value={formData.previousNames}
              onChange={(v) => updateField('previousNames', v)}
              placeholder="Enter previous names"
              half
            />
          </div>
          {/* Row 2 */}
          <div className="flex flex-col sm:flex-row gap-2 mt-2">
            <DateInput
              label="Date of Birth"
              value={formData.dateOfBirth}
              onChange={(v) => updateField('dateOfBirth', v)}
              half
            />
            <TextInput
              label="Place of Birth"
              value={formData.placeOfBirth}
              onChange={(v) => updateField('placeOfBirth', v)}
              placeholder="Enter place of birth"
              half
            />
          </div>
          {/* Row 3 */}
          <div className="flex flex-col sm:flex-row gap-2 mt-2">
            <TextInput
              label="Nationality"
              value={formData.nationality}
              onChange={(v) => updateField('nationality', v)}
              placeholder="Enter nationality"
              half
            />
            <TextInput
              label="Other Nationalities"
              value={formData.otherNationalities}
              onChange={(v) => updateField('otherNationalities', v)}
              placeholder="Enter other nationalities"
              half
            />
          </div>
          {/* Row 4 */}
          <div className="flex flex-col sm:flex-row gap-2 mt-2">
            <TextInput
              label="State of Origin"
              value={formData.stateOfOrigin}
              onChange={(v) => updateField('stateOfOrigin', v)}
              placeholder="Enter state of origin"
              half
            />
            <TextInput
              label="Local Government Area"
              value={formData.localGovernmentArea}
              onChange={(v) => updateField('localGovernmentArea', v)}
              placeholder="Enter LGA"
              half
            />
          </div>
          {/* Row 5 */}
          <div className="w-full md:w-[487px] mt-2">
            <TextInput
              label="National Identification Number"
              value={formData.nationalIdNumber}
              onChange={(v) => updateField('nationalIdNumber', v)}
              placeholder="Enter NIN"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function StepAddressHistory({
  formData,
  setFormData,
}: {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
  const updateAddress = (index: number, field: keyof AddressEntry, value: string) => {
    setFormData((prev) => ({
      ...prev,
      addresses: prev.addresses.map((addr, i) =>
        i === index ? { ...addr, [field]: value } : addr
      ),
    }));
  };

  const addAddress = () => {
    setFormData((prev) => ({
      ...prev,
      addresses: [...prev.addresses, { date: '', address: '' }],
    }));
  };

  const removeAddress = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      addresses: prev.addresses.filter((_, i) => i !== index),
    }));
  };

  const updateWorkHistory = (index: number, field: keyof WorkHistoryEntry, value: string) => {
    setFormData((prev) => ({
      ...prev,
      workHistory: prev.workHistory.map((entry, i) =>
        i === index ? { ...entry, [field]: value } : entry
      ),
    }));
  };

  const updateEducationHistory = (index: number, field: keyof EducationHistoryEntry, value: string) => {
    setFormData((prev) => ({
      ...prev,
      educationHistory: prev.educationHistory.map((entry, i) =>
        i === index ? { ...entry, [field]: value } : entry
      ),
    }));
  };

  return (
    <div className="flex flex-col gap-6 text-left">
      {/* Section: Address History */}
      <div className="flex flex-col gap-[23px]">
        <h2 className="text-2xl font-bold text-[#0f0f0f] leading-8">Address History</h2>
        <div className="flex flex-col gap-4">
          {formData.addresses.map((addr, i) => (
            <div key={i} className="flex flex-col sm:flex-row gap-2">
              <div className="w-full sm:w-[232px] shrink-0">
                <DateRangeInput
                  label="Date"
                  value={addr.date}
                  onChange={(v) => updateAddress(i, 'date', v)}
                />
              </div>
              <TextInput
                label="Address"
                value={addr.address}
                onChange={(v) => updateAddress(i, 'address', v)}
                placeholder="Enter address"
                half
              />
              {i > 0 && (
                <button
                  type="button"
                  onClick={() => removeAddress(i)}
                  className="self-end mb-1 shrink-0 w-10 h-10 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                  title="Remove address"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addAddress}
            className="flex items-center justify-center gap-2 bg-[#e0e0e0] rounded-[7px] h-12 w-full sm:w-[264px] text-sm text-[#151515]"
          >
            <Plus className="w-6 h-6" />
            Add new address
          </button>
        </div>
      </div>

      {/* Section: Work History */}
      <div className="flex flex-col gap-[23px]">
        <h2 className="text-2xl font-bold text-[#0f0f0f] leading-8">Work History</h2>
        <div className="flex flex-col gap-4">
          {formData.workHistory.map((entry, i) => (
            <div key={i} className="flex flex-col gap-2 p-4 border border-[#e0e0e0] rounded-lg">
              <div className="flex flex-col sm:flex-row gap-2">
                <TextInput label="Employer" value={entry.employer} onChange={(v) => updateWorkHistory(i, 'employer', v)} placeholder="Company name" half />
                <TextInput label="Address" value={entry.address} onChange={(v) => updateWorkHistory(i, 'address', v)} placeholder="Work address" half />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <TextInput label="Contact" value={entry.contact} onChange={(v) => updateWorkHistory(i, 'contact', v)} placeholder="+234..." half />
                <DateInput label="Start Date" value={entry.startDate} onChange={(v) => updateWorkHistory(i, 'startDate', v)} half />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <DateInput label="End Date" value={entry.endDate} onChange={(v) => updateWorkHistory(i, 'endDate', v)} half />
                <div className="flex-1 flex items-end">
                  <button type="button" onClick={() => setFormData((prev) => ({ ...prev, workHistory: prev.workHistory.filter((_, idx) => idx !== i) }))} className="mb-1 shrink-0 w-10 h-10 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors" title="Remove entry">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setFormData((prev) => ({
              ...prev,
              workHistory: [...prev.workHistory, { employer: '', address: '', contact: '', startDate: '', endDate: '' }],
            }))}
            className="flex items-center justify-center gap-2 bg-[#e0e0e0] rounded-[7px] h-12 w-full sm:w-[264px] text-sm text-[#151515]"
          >
            <Plus className="w-6 h-6" />
            Add work history
          </button>
        </div>
      </div>

      {/* Section: Education History */}
      <div className="flex flex-col gap-[23px]">
        <h2 className="text-2xl font-bold text-[#0f0f0f] leading-8">Education History</h2>
        <div className="flex flex-col gap-4">
          {formData.educationHistory.map((entry, i) => (
            <div key={i} className="flex flex-col gap-2 p-4 border border-[#e0e0e0] rounded-lg">
              <div className="flex flex-col sm:flex-row gap-2">
                <TextInput label="Institution" value={entry.institution} onChange={(v) => updateEducationHistory(i, 'institution', v)} placeholder="Institution name" half />
                <TextInput label="Address" value={entry.address} onChange={(v) => updateEducationHistory(i, 'address', v)} placeholder="Institution address" half />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <TextInput label="Contact" value={entry.contact} onChange={(v) => updateEducationHistory(i, 'contact', v)} placeholder="+234..." half />
                <DateInput label="Start Date" value={entry.startDate} onChange={(v) => updateEducationHistory(i, 'startDate', v)} half />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <DateInput label="End Date" value={entry.endDate} onChange={(v) => updateEducationHistory(i, 'endDate', v)} half />
                <div className="flex-1 flex items-end">
                  <button type="button" onClick={() => setFormData((prev) => ({ ...prev, educationHistory: prev.educationHistory.filter((_, idx) => idx !== i) }))} className="mb-1 shrink-0 w-10 h-10 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors" title="Remove entry">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setFormData((prev) => ({
              ...prev,
              educationHistory: [...prev.educationHistory, { institution: '', address: '', contact: '', startDate: '', endDate: '' }],
            }))}
            className="flex items-center justify-center gap-2 bg-[#e0e0e0] rounded-[7px] h-12 w-full sm:w-[264px] text-sm text-[#151515]"
          >
            <Plus className="w-6 h-6" />
            Add education history
          </button>
        </div>
      </div>
    </div>
  );
}

function StepCriminalDisclosure({
  formData,
  setFormData,
}: {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-[#0f0f0f] mb-6 leading-8">
        Criminal & Law Enforcement Disclosure
      </h2>
      <div className="flex flex-col gap-4">
        <RadioWithDetails
          label="Have you ever been convicted of a criminal offence in Nigeria or any other country?"
          value={formData.criminalConviction}
          onChange={(v) => setFormData((prev) => ({ ...prev, criminalConviction: v }))}
          showListField={false}
        />
        <RadioWithDetails
          label="Are you currently under investigation by any law enforcement or anti-corruption agency?"
          value={formData.underInvestigation}
          onChange={(v) => setFormData((prev) => ({ ...prev, underInvestigation: v }))}
          showListField={false}
        />
        <RadioWithDetails
          label="Have you ever been charged with financial crimes, fraud, bribery, money laundering, or tax evasion?"
          value={formData.financialCrimes}
          onChange={(v) => setFormData((prev) => ({ ...prev, financialCrimes: v }))}
          showListField={false}
        />
        <RadioWithDetails
          label="Have you ever entered into a plea bargain in any jurisdiction?"
          value={formData.pleaBargain}
          onChange={(v) => setFormData((prev) => ({ ...prev, pleaBargain: v }))}
          showListField={false}
        />
        <RadioWithDetails
          label="Have you been disengaged, terminated, or asked to resign from any position?"
          value={formData.hasBeenDisengaged}
          onChange={(v) => setFormData((prev) => ({ ...prev, hasBeenDisengaged: v }))}
          showListField={false}
        />
      </div>
    </div>
  );
}

function StepFinancialDirectorship({
  formData,
  setFormData,
}: {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-[#0f0f0f] mb-6 leading-8">
        Financial & Directorship History
      </h2>
      <div className="flex flex-col gap-4">
        <RadioWithDetails
          label="Have you served as a director of a company that was liquidated or struck off?"
          value={formData.directorLiquidated}
          onChange={(v) => setFormData((prev) => ({ ...prev, directorLiquidated: v }))}
          showListField={false}
        />

        <RadioWithDetails
          label="Have you been disqualified from acting as a director?"
          value={formData.disqualifiedDirector}
          onChange={(v) => setFormData((prev) => ({ ...prev, disqualifiedDirector: v }))}
          showListField={false}
        />

        <RadioWithDetails
          label="Are you currently a director, shareholder, or beneficial owner in any company? (List all)"
          value={formData.currentDirector}
          onChange={(v) => setFormData((prev) => ({ ...prev, currentDirector: v }))}
          showListField={false}
        />
      </div>
    </div>
  );
}

function StepProfessionalDiscipline({
  formData,
  setFormData,
}: {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-[#0f0f0f] mb-6 leading-8">Professional Discipline</h2>
      <div className="flex flex-col gap-2 overflow-y-auto">
        <RadioWithDetails
          label="Are you subject to any professional disciplinary proceedings?"
          value={formData.disciplinaryProceedings}
          onChange={(v) => setFormData((prev) => ({ ...prev, disciplinaryProceedings: v }))}
        />
        <RadioWithDetails
          label="Have you ever had a professional licence revoked or suspended?"
          value={formData.licenceRevoked}
          onChange={(v) => setFormData((prev) => ({ ...prev, licenceRevoked: v }))}
        />
        <RadioWithDetails
          label="Have you been sanctioned by any Nigerian regulatory body?"
          value={formData.regulatorySanction}
          onChange={(v) => setFormData((prev) => ({ ...prev, regulatorySanction: v }))}
        />
      </div>
    </div>
  );
}

function StepPoliticalExposure({
  formData,
  setFormData,
}: {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
  const updateField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-[#0f0f0f] mb-6 leading-8">Political Exposure</h2>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold text-[#151515] leading-6">
            Are you a Politically Exposed Person (PEP)?
          </label>
          <RadioGroup
            value={formData.isPep}
            onChange={(v) => updateField('isPep', v)}
          />
        </div>

        <RadioWithDetails
          label="Have you held any public office?"
          value={formData.hasHeldPublicOffice}
          onChange={(v) => setFormData((prev) => ({ ...prev, hasHeldPublicOffice: v }))}
          showListField={false}
        />

        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold text-[#151515] leading-6">
            Are you related to or closely associated with a PEP?
          </label>
          <RadioGroup
            value={formData.isRelatedToPep}
            onChange={(v) => updateField('isRelatedToPep', v)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold text-[#151515] leading-6">
            Have you been involved in any procurement-related investigation?
          </label>
          <RadioGroup
            value={formData.hasProcurementInvestigation}
            onChange={(v) => updateField('hasProcurementInvestigation', v)}
          />
        </div>
      </div>
    </div>
  );
}

function StepWorkplaceConduct({
  formData,
  setFormData,
}: {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
  const updateField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-[#0f0f0f] mb-6 leading-8">Workplace Conduct</h2>
      <div className="flex flex-col gap-4">
        <RadioWithDetails
          label="Have you been dismissed for gross misconduct?"
          value={formData.hasMisconductDismissal}
          onChange={(v) => setFormData((prev) => ({ ...prev, hasMisconductDismissal: v }))}
          showListField={false}
        />

        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold text-[#151515] leading-6">
            Have you been subject to any workplace investigation?
          </label>
          <RadioGroup
            value={formData.hasWorkplaceInvestigation}
            onChange={(v) => updateField('hasWorkplaceInvestigation', v)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold text-[#151515] leading-6">
            Have you worked with vulnerable groups?
          </label>
          <RadioGroup
            value={formData.hasWorkedWithVulnerableGroups}
            onChange={(v) => updateField('hasWorkedWithVulnerableGroups', v)}
          />
        </div>

        <RadioWithDetails
          label="Have you been barred from holding a leadership position?"
          value={formData.hasLeadershipBar}
          onChange={(v) => setFormData((prev) => ({ ...prev, hasLeadershipBar: v }))}
          showListField={false}
        />
      </div>
    </div>
  );
}

function StepCivilClaims({
  formData,
  setFormData,
}: {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
  const updateField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const questions = [
    {
      label: 'Have you been involved in shareholder/fiduciary disputes?',
      field: 'hasShareholderDisputes' as keyof FormData,
    },
    {
      label: 'Have you had any judgments entered against you?',
      field: 'hasJudgments' as keyof FormData,
    },
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold text-[#0f0f0f] mb-6 leading-8">Civil Claims</h2>
      <div className="flex flex-col gap-4">
        {questions.map((q) => (
          <div key={q.field} className="flex flex-col gap-2">
            <label className="text-sm font-bold text-[#151515] leading-6">{q.label}</label>
            <RadioGroup
              value={formData[q.field] as string}
              onChange={(v) => updateField(q.field, v)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function StepMediaDisclosure({
  formData,
  setFormData,
}: {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
  const updateField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-[#0f0f0f] mb-6 leading-8">Media Disclosure</h2>
      <div className="flex flex-col gap-4">
        <RadioWithDetails
          label="Have you been the subject of adverse media coverage?"
          value={formData.hasAdverseMedia}
          onChange={(v) => setFormData((prev) => ({ ...prev, hasAdverseMedia: v }))}
          showListField={false}
        />

        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold text-[#151515] leading-6">
            Have you been subject to public accusation?
          </label>
          <RadioGroup
            value={formData.hasPublicAccusation}
            onChange={(v) => updateField('hasPublicAccusation', v)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold text-[#151515] leading-6">
            Are there any undisclosed reputational matters?
          </label>
          <RadioGroup
            value={formData.hasUndisclosedReputationalMatters}
            onChange={(v) => updateField('hasUndisclosedReputationalMatters', v)}
          />
        </div>
      </div>
    </div>
  );
}

function StepConflictOfInterest({
  formData,
  setFormData,
}: {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-[#0f0f0f] mb-6 leading-8">Conflict Of Interest</h2>
      <div className="flex flex-col gap-2">
        <RadioWithDetails
          label="Do you have any business conflict of interest?"
          value={formData.hasBusinessConflict}
          onChange={(v) => setFormData((prev) => ({ ...prev, hasBusinessConflict: v }))}
          showListField={false}
        />

        <RadioWithDetails
          label="Do you have any procurement relationship that may pose a conflict?"
          value={formData.hasProcurementRelationship}
          onChange={(v) => setFormData((prev) => ({ ...prev, hasProcurementRelationship: v }))}
          showListField={false}
        />

        <RadioWithDetails
          label="Do you sit on any competing boards?"
          value={formData.hasCompetingBoard}
          onChange={(v) => setFormData((prev) => ({ ...prev, hasCompetingBoard: v }))}
          showListField={false}
        />
      </div>
    </div>
  );
}

function StepConsentAgreement({
  formData,
  setFormData,
}: {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
  const toggleConsent = (item: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      consentItems: { ...prev.consentItems, [item]: checked },
    }));
  };

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-2xl font-bold text-[#0f0f0f] leading-8">
        SECURITY CLEARANCE CONSENT AND AGREEMENT.
      </h2>
      <p className="text-sm text-[#1b1b1b] leading-5">I consent and agree to:</p>
      <div className="flex flex-col gap-4">
        {CONSENT_ITEMS.map((item) => (
          <Checkbox
            key={item}
            label={item}
            checked={formData.consentItems[item] ?? false}
            onChange={(v) => toggleConsent(item, v)}
          />
        ))}
      </div>
      <p className="text-sm text-[#1b1b1b] leading-5">
        You understand false declarations may result in termination or withdrawal of offer.
      </p>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function EmployeeConsent() {
  const { linkToken } = useParams<{ linkToken: string }>();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const {
    data: investigation,
    isLoading: loadingInvestigation,
    error: investigationError,
  } = useGetInvestigationByLinkQuery(linkToken || '', { skip: !linkToken });

  const [submitForm, { isLoading: submitting }] = useSubmitEmployeeFormMutation();

  useEffect(() => {
    if (!linkToken) {
      navigate('/');
    }
  }, [linkToken, navigate]);

  // Show success view if form was already submitted
  useEffect(() => {
    if (investigation?.isFormSubmitted) {
      setShowSuccess(true);
    }
  }, [investigation]);

  // Prefill form with employee details from investigation
  useEffect(() => {
    if (investigation && !investigation.isFormSubmitted) {
      setFormData((prev) => ({
        ...prev,
        fullLegalName: [
          investigation.employeeFirstName,
          investigation.employeeMiddleName,
          investigation.employeeLastName,
        ]
          .filter(Boolean)
          .join(' '),
        previousNames: investigation.employeeMiddleName || '',
        dateOfBirth: investigation.dateOfBirth || '',
        nationality: investigation.nationality || '',
        otherNationalities: investigation.otherNationalities || '',
        nationalIdNumber: investigation.nationalIdentificationNumber || '',
        stateOfOrigin: investigation.stateOfOrigin || '',
        localGovernmentArea: investigation.localGovernmentArea || '',
        placeOfBirth: investigation.placeOfBirth || '',
      }));
    }
  }, [investigation]);

  const getReadableError = (err: any, fallback = 'Failed to submit form') => {
    const serverMessage = err?.data?.message ?? err?.message ?? err;
    if (Array.isArray(serverMessage)) return serverMessage.join(', ');
    if (typeof serverMessage === 'string') return serverMessage;
    if (serverMessage && typeof serverMessage === 'object')
      return serverMessage.message || serverMessage.error || JSON.stringify(serverMessage);
    return fallback;
  };

  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const formScrollRef = useRef<HTMLDivElement>(null);

  const validateStep = (step: number): string[] => {
    const errors: string[] = [];
    const requireRadio = (value: string, label: string) => {
      if (!value) errors.push(label);
    };
    const requireRadioDetails = (value: RadioWithDetailsValue, label: string) => {
      if (!value.answer) errors.push(label);
      if (value.answer === 'yes' && !value.reasons.trim()) errors.push(`${label} — please provide details`);
    };

    switch (step) {
      case 0: // Personal Identification
        if (!formData.fullLegalName.trim()) errors.push('Full Legal Name is required');
        if (!formData.dateOfBirth) errors.push('Date of Birth is required');
        if (!formData.placeOfBirth.trim()) errors.push('Place of Birth is required');
        if (!formData.nationality.trim()) errors.push('Nationality is required');
        if (!formData.stateOfOrigin.trim()) errors.push('State of Origin is required');
        if (!formData.localGovernmentArea.trim()) errors.push('Local Government Area is required');
        break;
      case 1: // Address & History
        formData.addresses.forEach((addr, i) => {
          if (!addr.date.trim()) errors.push(`Address ${i + 1}: Date is required`);
          if (!addr.address.trim()) errors.push(`Address ${i + 1}: Address is required`);
        });
        break;
      case 2: // Criminal & Law Enforcement Disclosure
        requireRadioDetails(formData.criminalConviction, 'Criminal conviction question');
        requireRadioDetails(formData.underInvestigation, 'Under investigation question');
        requireRadioDetails(formData.financialCrimes, 'Financial crimes question');
        requireRadioDetails(formData.pleaBargain, 'Plea bargain question');
        requireRadioDetails(formData.hasBeenDisengaged, 'Disengagement question');
        break;
      case 3: // Financial & Directorship History
        requireRadioDetails(formData.directorLiquidated, 'Director of liquidated company question');
        requireRadioDetails(formData.disqualifiedDirector, 'Disqualified director question');
        requireRadioDetails(formData.currentDirector, 'Current director/shareholder question');
        break;
      case 4: // Professional Discipline
        requireRadioDetails(formData.disciplinaryProceedings, 'Disciplinary proceedings question');
        requireRadioDetails(formData.licenceRevoked, 'Licence revoked question');
        requireRadioDetails(formData.regulatorySanction, 'Regulatory sanction question');
        break;
      case 5: // Political Exposure
        requireRadio(formData.isPep, 'PEP question is required');
        requireRadioDetails(formData.hasHeldPublicOffice, 'Public office question');
        requireRadio(formData.isRelatedToPep, 'Related to PEP question is required');
        requireRadio(formData.hasProcurementInvestigation, 'Procurement investigation question is required');
        break;
      case 6: // Workplace Conduct
        requireRadioDetails(formData.hasMisconductDismissal, 'Misconduct dismissal question');
        requireRadio(formData.hasWorkplaceInvestigation, 'Workplace investigation question is required');
        requireRadio(formData.hasWorkedWithVulnerableGroups, 'Vulnerable groups question is required');
        requireRadioDetails(formData.hasLeadershipBar, 'Leadership bar question');
        break;
      case 7: // Civil Claims
        requireRadio(formData.hasShareholderDisputes, 'Shareholder disputes question is required');
        requireRadio(formData.hasJudgments, 'Judgments question is required');
        break;
      case 8: // Media Disclosure
        requireRadioDetails(formData.hasAdverseMedia, 'Adverse media question');
        requireRadio(formData.hasPublicAccusation, 'Public accusation question is required');
        requireRadio(formData.hasUndisclosedReputationalMatters, 'Undisclosed reputational matters question is required');
        break;
      case 9: // Conflict of Interest
        requireRadioDetails(formData.hasBusinessConflict, 'Business conflict question');
        requireRadioDetails(formData.hasProcurementRelationship, 'Procurement relationship question');
        requireRadioDetails(formData.hasCompetingBoard, 'Competing board question');
        break;
      case 10: // Consent
        if (!Object.values(formData.consentItems).every(Boolean)) {
          errors.push('You must agree to all consent items before submitting');
        }
        break;
    }
    return errors;
  };

  const handleNext = () => {
    const errors = validateStep(currentStep);
    setValidationErrors(errors);
    if (errors.length > 0) {
      formScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (currentStep < TOTAL_STEPS - 1) {
      setCurrentStep((prev) => prev + 1);
      formScrollRef.current?.scrollTo({ top: 0 });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setValidationErrors([]);
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!linkToken) return;
    const errors = validateStep(currentStep);
    setValidationErrors(errors);
    if (errors.length > 0) {
      formScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSubmitError('');

    try {
      await submitForm({
        linkToken,
        data: (() => {
          const fd = new FormData();
          // Section 1: Personal Identification
          fd.append('fullLegalName', formData.fullLegalName);
          fd.append('previousNames', formData.previousNames);
          fd.append('dateOfBirth', formData.dateOfBirth);
          fd.append('placeOfBirth', formData.placeOfBirth);
          fd.append('nationality', formData.nationality);
          fd.append('stateOfOrigin', formData.stateOfOrigin);
          fd.append('localGovernmentArea', formData.localGovernmentArea);
          fd.append('otherNationalities', formData.otherNationalities);
          fd.append('nationalIdentificationNumber', formData.nationalIdNumber);
          fd.append('addressHistory', JSON.stringify(formData.addresses));
          fd.append('workHistory', JSON.stringify(formData.workHistory));
          fd.append('educationHistory', JSON.stringify(formData.educationHistory));
          // Section 2: Criminal & Law Enforcement
          fd.append('hasConviction', formData.criminalConviction.answer);
          fd.append('convictionDetails', formData.criminalConviction.reasons);
          fd.append('isUnderInvestigation', formData.underInvestigation.answer);
          fd.append('investigationDetails', formData.underInvestigation.reasons);
          fd.append('hasFinancialCrimeCharge', formData.financialCrimes.answer);
          fd.append('financialCrimeDetails', formData.financialCrimes.reasons);
          fd.append('hasPleaBargain', formData.pleaBargain.answer);
          fd.append('pleaBargainDetails', formData.pleaBargain.reasons);
          fd.append('hasBeenDisengaged', formData.hasBeenDisengaged.answer);
          fd.append('disengagementDetails', formData.hasBeenDisengaged.reasons);
          // Section 3: Financial & Directorship
          fd.append('hasLiquidatedCompany', formData.directorLiquidated.answer);
          fd.append('liquidatedCompanyDetails', formData.directorLiquidated.reasons);
          fd.append('hasDirectorDisqualification', formData.disqualifiedDirector.answer);
          fd.append('directorDisqualificationDetails', formData.disqualifiedDirector.reasons);
          fd.append('isCurrentDirector', formData.currentDirector.answer);
          fd.append('currentDirectorshipDetails', formData.currentDirector.reasons);
          // Section 4: Professional Discipline
          fd.append('hasDisciplinaryProceedings', formData.disciplinaryProceedings.answer);
          fd.append('disciplinaryProceedingsDetails', formData.disciplinaryProceedings.reasons);
          fd.append('hasLicenceRevoked', formData.licenceRevoked.answer);
          fd.append('licenceRevokedDetails', formData.licenceRevoked.reasons);
          fd.append('revokedLicences', formData.licenceRevoked.list ? JSON.stringify([formData.licenceRevoked.list]) : '');
          fd.append('hasRegulatoryBodySanction', formData.regulatorySanction.answer);
          fd.append('regulatoryBodySanctionDetails', formData.regulatorySanction.reasons);
          // Section 5: Political Exposure
          fd.append('isPep', formData.isPep);
          fd.append('hasHeldPublicOffice', formData.hasHeldPublicOffice.answer);
          fd.append('publicOfficeDetails', formData.hasHeldPublicOffice.reasons);
          fd.append('isRelatedToPep', formData.isRelatedToPep);
          fd.append('hasProcurementInvestigation', formData.hasProcurementInvestigation);
          // Section 6: Workplace Conduct
          fd.append('hasMisconductDismissal', formData.hasMisconductDismissal.answer);
          fd.append('misconductDismissalDetails', formData.hasMisconductDismissal.reasons);
          fd.append('hasWorkplaceInvestigation', formData.hasWorkplaceInvestigation);
          fd.append('hasWorkedWithVulnerableGroups', formData.hasWorkedWithVulnerableGroups);
          fd.append('hasLeadershipBar', formData.hasLeadershipBar.answer);
          fd.append('leadershipBarDetails', formData.hasLeadershipBar.reasons);
          // Section 7: Civil Claims
          fd.append('hasShareholderDisputes', formData.hasShareholderDisputes);
          fd.append('hasJudgments', formData.hasJudgments);
          // Section 8: Media Disclosure
          fd.append('hasAdverseMedia', formData.hasAdverseMedia.answer);
          fd.append('adverseMediaDetails', formData.hasAdverseMedia.reasons);
          fd.append('hasPublicAccusation', formData.hasPublicAccusation);
          fd.append('hasUndisclosedReputationalMatters', formData.hasUndisclosedReputationalMatters);
          // Section 9: Conflict of Interest
          fd.append('hasBusinessConflict', formData.hasBusinessConflict.answer);
          fd.append('businessConflictDetails', formData.hasBusinessConflict.reasons);
          fd.append('hasProcurementRelationship', formData.hasProcurementRelationship.answer);
          fd.append('procurementRelationshipDetails', formData.hasProcurementRelationship.reasons);
          fd.append('hasCompetingBoard', formData.hasCompetingBoard.answer);
          fd.append('competingBoardDetails', formData.hasCompetingBoard.reasons);
          // Section 10: Consent
          fd.append('consentCriminalBackground', String(formData.consentItems['Criminal background verification'] ?? false));
          fd.append('consentEmploymentReference', String(formData.consentItems['Employment reference checks'] ?? false));
          fd.append('consentGuarantorReference', String(formData.consentItems['Reference/Guarantor verification'] ?? false));
          fd.append('consentAddressVerification', String(formData.consentItems['Address verification'] ?? false));
          fd.append('consentSocialMediaReview', String(formData.consentItems['Social media review'] ?? false));
          fd.append('consentCourtRecordSearches', String(formData.consentItems['Court record searches'] ?? false));
          fd.append('consentLifestyleAudit', String(formData.consentItems['Surprise Lifestyle Audit'] ?? false));
          fd.append('consentProcurementIntegrityChecks', String(formData.consentItems['Surprise Procurement Integrity Checks'] ?? false));
          return fd;
        })(),
      }).unwrap();

      setShowSuccess(true);
    } catch (error: any) {
      setSubmitError(getReadableError(error));
    }
  };

  const isLastStep = currentStep === TOTAL_STEPS - 1;
  const activeTabIndex = STEP_TO_TAB[currentStep] ?? 0;
  const employeeName = investigation
    ? `${investigation.employeeFirstName} ${investigation.employeeLastName}`
    : '';

  // ── Loading State ──────────────────────────────────────────────────────────

  if (loadingInvestigation) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-gray-900 animate-spin mx-auto mb-4" />
          <p className="text-[#4e4d4d]">Loading investigation details...</p>
        </div>
      </div>
    );
  }

  // ── Error State ────────────────────────────────────────────────────────────

  if (investigationError || !investigation) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#151515] mb-2">Invalid Link</h2>
          <p className="text-[#4e4d4d] mb-6">
            This investigation link is invalid or has expired. Please contact your employer for a
            new link.
          </p>
          <Button onClick={() => navigate('/')} className="bg-[#151515] hover:bg-[#1b1b1b]">
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center p-4 md:p-10">
      <div className="bg-white rounded-2xl md:rounded-3xl w-full max-w-[1840px] min-h-screen md:min-h-0 md:h-[1021px] relative overflow-hidden flex flex-col">
        {/* ── Decorative Background Image - Top Right ────────────────── */}
        <img
          src={appliBg}
          alt=""
          className="hidden lg:block absolute top-0 right-0 h-[420px] w-auto rounded-tr-3xl object-contain"
        />

        {/* White gradient overlay (left-to-right fade over image) */}
        <div
          className="hidden lg:block absolute top-0 right-0 h-[420px] w-[726px] rounded-tr-3xl"
          style={{
            backgroundImage:
              'linear-gradient(92deg, rgb(255,255,255) 14%, rgba(255,255,255,0.886) 48%, rgba(255,255,255,0) 110%)',
          }}
        />


        {/* ── Hero / Greeting Section ────────────────────────────────── */}
        <div className="relative z-10 px-6 md:px-12 lg:px-[156px] pt-8 md:pt-[50px] text-left">
          <p className="text-xl md:text-2xl font-bold text-[#acacac] leading-9">
            Hi {investigation.employeeFirstName}
          </p>
          <h1 className="text-2xl md:text-[40px] font-bold text-[#151515] leading-tight md:leading-[30px] mt-3">
            Lets get more done today.
          </h1>
        </div>

        {/* ── Description ────────────────────────────────────────────── */}
        <p className="relative z-10 px-6 md:px-12 lg:px-[156px] mt-6 md:mt-10 text-sm text-[#4e4d4d] leading-5 text-justify">
          Your employer has requested a background check as part of their hiring or employment
          process. This check will verify the information you provide below and may include
          verification of your identity, employment history, and other relevant credentials. All
          information you provide will be handled confidentially and in accordance with applicable
          data protection regulations.
        </p>

        {/* ── Form Content Section ──────────────────────────────────── */}
        <div ref={formScrollRef} className="relative z-10 flex-1 overflow-x-hidden overflow-y-auto text-left px-6 md:px-12 lg:px-[156px] mt-6 md:mt-10 pb-4 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {showSuccess ? (
            /* ── Success State ────────────────────────────────────────── */
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-[88px] h-[88px] mb-6">
                <svg viewBox="0 0 88 88" fill="none" className="w-full h-full">
                  <circle cx="44" cy="44" r="40" fill="#e8f5e9" />
                  <path
                    d="M28 44l12 12 20-20"
                    stroke="#4caf50"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="44" cy="44" r="40" stroke="#4caf50" strokeWidth="2" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-[#151515] mb-4">Submission Successful</h2>
              <p className="text-sm text-black leading-6 max-w-[561px]">
                Thank you for providing your information and consent. The background check process
                will now proceed. You will be notified of any updates regarding your background
                check status. If you have any questions, please contact your employer directly.
              </p>
            </div>
          ) : (
            /* ── Step Content ─────────────────────────────────────────── */
            <>
              {validationErrors.length > 0 && (
                <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm font-bold text-orange-700 mb-2">Please complete the following:</p>
                  <ul className="list-disc list-inside flex flex-col gap-1">
                    {validationErrors.map((err, i) => (
                      <li key={i} className="text-sm text-orange-600">{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              {submitError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{submitError}</p>
                </div>
              )}

              {currentStep === 0 && (
                <StepPersonalIdentification formData={formData} setFormData={setFormData} />
              )}
              {currentStep === 1 && (
                <StepAddressHistory formData={formData} setFormData={setFormData} />
              )}
              {currentStep === 2 && (
                <StepCriminalDisclosure formData={formData} setFormData={setFormData} />
              )}
              {currentStep === 3 && (
                <StepFinancialDirectorship formData={formData} setFormData={setFormData} />
              )}
              {currentStep === 4 && (
                <StepProfessionalDiscipline formData={formData} setFormData={setFormData} />
              )}
              {currentStep === 5 && (
                <StepPoliticalExposure formData={formData} setFormData={setFormData} />
              )}
              {currentStep === 6 && (
                <StepWorkplaceConduct formData={formData} setFormData={setFormData} />
              )}
              {currentStep === 7 && (
                <StepCivilClaims formData={formData} setFormData={setFormData} />
              )}
              {currentStep === 8 && (
                <StepMediaDisclosure formData={formData} setFormData={setFormData} />
              )}
              {currentStep === 9 && (
                <StepConflictOfInterest formData={formData} setFormData={setFormData} />
              )}
              {currentStep === 10 && (
                <StepConsentAgreement formData={formData} setFormData={setFormData} />
              )}

            </>
          )}
        </div>

        {/* ── Bottom Navigation ──────────────────────────────────────── */}
        {!showSuccess && (
          <div className="relative z-10 px-6 md:px-12 lg:px-[156px] py-4 md:py-[34px] flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 border-t border-gray-100 md:border-t-0">
            {/* Previous Button */}
            {currentStep === 0 ? (
              <button
                type="button"
                className="bg-[#f1f1f1] rounded-[10px] h-10 px-5 text-sm text-[#151515] shrink-0"
              >
                Previous Page
              </button>
            ) : (
              <button
                type="button"
                onClick={handlePrevious}
                className="bg-[#f1f1f1] rounded-[10px] h-10 px-5 text-sm text-[#151515] shrink-0"
              >
                Previous
              </button>
            )}

            {/* Tab Pills */}
            <div className="flex gap-2 md:gap-4 items-center overflow-x-auto overflow-y-hidden flex-1 h-10 scrollbar-hide">
              {TABS.map((tab, i) => {
                const isActive = i === activeTabIndex;
                return (
                  <button
                    key={tab}
                    type="button"
                    className={`shrink-0 px-3 md:px-4 py-2 md:py-3 rounded-full text-[10px] md:text-xs text-[#1b1b1b] whitespace-nowrap leading-5 ${
                      isActive
                        ? 'bg-[#dcdcdc]'
                        : 'border border-[#dcdcdc]'
                    }`}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>

            {/* Next / Submit Button */}
            {isLastStep ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-[#151515] rounded-lg h-10 w-full sm:w-[125px] text-sm font-bold text-white text-center shrink-0 disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                ) : (
                  'Submit'
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                className="bg-[#151515] rounded-lg h-10 w-full sm:w-[125px] text-sm font-bold text-white text-center shrink-0"
              >
                Next
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
